import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { formatMoney, unitDivisor } from '../../util/currency';
import { richText } from '../../util/richText';
import { types as sdkTypes } from '../../util/sdkLoader';
import { isBookingProcessAlias } from '../../transactions/transaction';
import {
  formatWholeUnitPrice,
  getDesignerLabel,
  getMultiEnumLabels,
  getSizeLabel,
} from '../../util/listingDisplay';
import { getListingCapabilities } from '../../util/dualListing';

import css from './ListingCard.module.css';

const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTooltip: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values:
 *   - titlePlain: raw title string (for aria/alt)
 *   - titleFormatted: React nodes from richText(title) for display
 *   - showPrice: whether to show the price block
 *   - priceTooltip: string for the price element's title attribute (tooltip on hover)
 *   - priceMessage: string or null for the price block content (same translation as used in cardAriaLabel when shown)
 *   - cardAriaLabel: ready-to-use aria-label for the card link (listing title + price line when shown)
 *   - authorName: "ListingCard.author" string containing author's display name
 */
export const getListingCardTranslations = (listing, config, intl) => {
  const { title = '', price, publicData } = listing?.attributes || {};

  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: authorDisplayName }
  );

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const { formattedPrice, priceTooltip } = priceData(price, config.currency, intl);

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const priceMessageId = hasMultiplePriceVariants
    ? 'ListingCard.priceStartingFrom'
    : 'ListingCard.price';

  const perUnitString = isBookable
    ? intl.formatMessage({ id: 'ListingCard.perUnit' }, { unitType: publicData?.unitType })
    : '';

  // Single formatted price line (amount + per-unit if applicable) for the price block.
  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? <span className={css.perUnit}>{perUnitString}</span> : '';
  const hasPrice = showPrice && formattedPrice != null;
  const priceMessage = hasPrice
    ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
    : '';

  // Plain-string twin of the above, for the aria-label. The display version
  // interpolates React elements, so intl returns an array of nodes — putting that
  // into an aria-label stringifies the elements as "[object Object]".
  const priceMessagePlain = hasPrice
    ? intl.formatMessage(
        { id: priceMessageId },
        { priceValue: formattedPrice, pricePerUnit: perUnitString }
      )
    : '';

  // "RRP $495" — struck through next to the rate, giving the rental price context.
  const formattedRetailValue = formatWholeUnitPrice(
    publicData?.retail_value,
    config.currency,
    intl
  );
  const retailMessage = formattedRetailValue
    ? intl.formatMessage({ id: 'ListingCard.retailValue' }, { retailValue: formattedRetailValue })
    : null;

  // A card for a listing that can also be bought shows the purchase price
  // alongside the rate, so the option is visible before opening the listing.
  const capabilities = getListingCapabilities(publicData);
  const formattedPurchasePrice = formatWholeUnitPrice(
    publicData?.purchase_price,
    config.currency,
    intl
  );
  const buyMessage =
    capabilities.both && formattedPurchasePrice
      ? intl.formatMessage({ id: 'ListingCard.buyPrice' }, { price: formattedPurchasePrice })
      : null;

  // Card headline is the designer, with the item name demoted beneath it —
  // in fashion the label is what people scan for. Falls back to the title
  // when a listing has no designer set.
  const listingFields = config.listing.listingFields || [];
  const designerLabel = getDesignerLabel(listingFields, publicData);
  const sizeLabel = getSizeLabel(listingFields, publicData);
  const occasionTags = getMultiEnumLabels(listingFields, 'occasion', publicData?.occasion);

  // "Beige Dress, UK 10". Avoids repeating the title when it is already the headline.
  const primaryLine = designerLabel || title;
  const detailParts = [designerLabel ? title : null, sizeLabel].filter(Boolean);
  const secondaryLine = detailParts.length > 0 ? detailParts.join(', ') : null;

  // Screen readers get the retail context too, so the rate isn't read without it.
  const spokenPrice = retailMessage ? `${retailMessage}, ${priceMessagePlain}` : priceMessagePlain;
  // Mirrors what's actually rendered: "Rixo, The Tallulah Dress, UK 10".
  const spokenTitle = secondaryLine ? `${primaryLine}, ${secondaryLine}` : primaryLine;
  const cardAriaLabel =
    priceMessagePlain.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: spokenTitle, formattedPrice: spokenPrice }
        )
      : spokenTitle;

  return {
    titlePlain: title,
    titleFormatted: richText(primaryLine, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    titleAsName: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    secondaryLine,
    occasionTags,
    authorName,
    showPrice,
    priceTooltip,
    priceMessage,
    formattedPrice,
    formattedRetailValue,
    isBookable,
    buyMessage,
    retailMessage,
    cardAriaLabel,
  };
};
